import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Backup, BackupService } from '../../../core/backup';
import { EMPTY, Subscription, timer } from 'rxjs';
import { Site, SiteService, SiteStatus } from '../../../core/site';
import { switchMap, debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { SelectionModel } from '@angular/cdk/collections';
import { StagingService } from '../../../core/staging';
import { ErrorDialog } from '../../dialog';

@Component({
  selector: 'app-staging-from-backup-table',
  templateUrl: './staging-from-backup-table.component.html',
  styleUrls: ['./staging-from-backup-table.component.scss'],
  standalone: false,
})
export class StagingFromBackupTableComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() isStaging = false;
  @Input() parentDialogRef = null;
  displayedColumns = [
    'date',
    'description',
    'fileSystemSize',
    'databaseSize',
    'posts',
    'pages',
    'plugins',
    'wpVersion',
  ];
  dataSource = new MatTableDataSource();

  siteId: string;
  numberOfBackups = 0;
  loading = true;
  siteAvailable = false;
  backups: Backup[] = [];
  selection = new SelectionModel<Backup>(false, null);
  subscriptions: Subscription[] = [];
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  private errDialogRef: MatDialogRef<ErrorDialog> = null;

  constructor(
    private backupService: BackupService,
    private siteService: SiteService,
    private matDialog: MatDialog,
    private stagingService: StagingService,
  ) {}

  ngOnInit(): void {
    this.setSiteId();
    this.pollSiteStatus();
    this.getNumberOfBackups();
    this.getListInitial();
  }

  setSiteId(): void {
    this.siteService.get().subscribe((site: Site) => {
      this.siteId = site.siteId;
    });
  }

  getNumberOfBackups(): void {
    this.subscriptions.push(
      this.backupService.numberOfBackups(this.siteId, this.isStaging).subscribe((numberOfBackups) => {
        this.numberOfBackups = numberOfBackups;
      }),
    );
  }

  getListInitial(): void {
    this.subscriptions.push(
      this.backupService
        .list(this.siteId, this.paginator.pageSize, 0, this.isStaging)
        .subscribe((backups: Backup[]) => {
          this.loading = false;
          this.paginator.pageIndex = 0;
          this.backups = backups;
          this.dataSource.data = backups.slice(0, this.paginator.pageSize);
        }),
    );
  }

  ngAfterViewInit(): void {
    this.subscriptions.push(
      this.paginator.page
        .pipe(
          debounceTime(250),
          switchMap((pe: PageEvent) => {
            return pe
              ? this.backupService.list(this.siteId, pe.pageSize, pe.pageSize * pe.pageIndex, this.isStaging).pipe(
                  map((backups: Backup[]) => {
                    return backups;
                  }),
                )
              : EMPTY;
          }),
        )
        .subscribe((backups: Backup[]) => {
          this.backups = backups;
          this.dataSource.data = backups.slice(0, this.paginator.pageSize);
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  createStagingFromBackup(): void {
    this.subscriptions.push(
      this.backupService.createStagingFromBackup(this.siteId, this.selection.selected[0].dateTime).subscribe(
        () => {
          this.stagingService.stagingFromBackup();
        },
        () =>
          this.displayError('There was an error creating your staging environment from a backup.  Please try again'),
        () => this.parentDialogRef.close(true),
      ),
    );
  }

  displayError(error: string): void {
    if (this.errDialogRef !== null) {
      return;
    }
    this.errDialogRef = this.matDialog.open(ErrorDialog, {
      data: {
        errMsg: error,
        overrideButtonKey: 'BUTTONS.OK',
      },
    });
    this.errDialogRef.afterClosed().subscribe(() => {
      this.errDialogRef = null;
    });
  }

  protected pollSiteStatus(): void {
    this.subscriptions.push(
      timer(5000)
        .pipe(
          switchMap(() => this.siteService.getStatus(this.siteId).pipe(map((ss: SiteStatus) => ss.available))),
          distinctUntilChanged(),
        )
        .subscribe(
          (available) => {
            this.siteAvailable = available;
          },
          () => {
            this.siteAvailable = false;
          },
        ),
    );
  }
}
